import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { Col, Flex, Row, Table, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../../Components/Button"
import EmptyComponent from "../../../Components/Empty"
import { DashboardFiltersBlock } from "../../../Components/Filters/DashboardFiltersBlock/DashboardFiltersBlock"
import SearchComponent from "../../../Components/Search"
import Icon from "../../../Icon"
import { getDates, useQuery } from "../../../Utils/utils"
import { CardTotalPanel } from "../components/CardTotalPanel"
import {
  alertsByBankDetailColumns,
  getDashboardProgressRowProps
} from "../components/Table/columns"
import Creators from "./../reducer"

import "../styles.less"

const { Title } = Typography

export const AlertsByBankPage = ({ usersViewForAdmin, userInfo, dashboard, ...props }) => {
  const pageRef = useRef()
  const navigate = useNavigate()
  const { search } = useLocation()
  let query = useQuery(search)
  const isMobile = useSelector((state) => state.nav.isMobile)
  let business_account_id = query.get("business_account_id")
  const { t } = useTranslation()
  const helpRef = useRef({ firstLoading: true })

  const businessId =
    usersViewForAdmin && business_account_id
      ? business_account_id
      : userInfo?.current_business_account_id

  const { filters, alertsBankData } = dashboard
  const extraParams = usersViewForAdmin ? { business_account_id: businessId } : {}

  // The reducer already puts `percentage` on each row (it paints the progress underline); the
  // average alert value is derived the same way the country breakdown derives its own.
  const tableRows = useMemo(
    () =>
      (alertsBankData?.result ?? []).map((row) => ({
        ...row,
        average: row.count ? row.volume / row.count : 0
      })),
    [alertsBankData]
  )

  const [bankSearch, setBankSearch] = useState("")
  const filteredRows = useMemo(() => {
    const term = bankSearch.trim().toLowerCase()
    if (!term) return tableRows
    return tableRows.filter((row) => (row.label ?? "").toLowerCase().includes(term))
  }, [tableRows, bankSearch])

  const handleChangeFilter = useCallback((newFilters) => {
    if (Array.isArray(newFilters.period)) {
      props.changeFilters({
        ...newFilters,
        period: { type: "custom", period: newFilters.period }
      })
    } else {
      props.changeFilters(newFilters)
    }
  }, [])

  useEffect(() => {
    document.title = "Dashboard - Chargeback"
    if (pageRef.current) pageRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [])

  useEffect(() => {
    if (filters?.period) {
      const period_query = getDates(filters.period)
      props.getAlertsBankData({
        chartType: "by_bank",
        ...period_query,
        ...extraParams
      })

      if (helpRef.current.firstLoading) helpRef.current.firstLoading = false
    }
  }, [filters?.period, businessId])

  const goBack = () => {
    if (usersViewForAdmin) {
      navigate("/admin/dashboard" + (search || ""))
    } else {
      navigate("/dashboard")
    }
  }

  return (
    // Vertical gutter only. A horizontal gutter gives the Row negative side margins, and the
    // page padding lives on the Row itself so nothing absorbs them: the Row then hangs 16px
    // past #main-content, which is overflow-x:auto, and the page scrolls sideways. The columns
    // are all span={24}, so they never needed a horizontal gutter. Dropping it keeps every
    // child at the same x (the -16px margin and the 16px column padding cancelled each other).
    <Row
      ref={pageRef}
      gutter={[0, isMobile ? 12 : 32]}
      className={cls("dashboard-page", {
        "dashboard-page_users_view_for_admin": usersViewForAdmin,
        isMobile: isMobile
      })}
    >
      <Col
        span={24}
        // Careful: React writes `false` as an empty string, so `padding: isMobile && 'unset'`
        // would strip the column's padding on desktop. Only send the overrides when they apply.
        style={
          isMobile
            ? { width: "100%", maxWidth: "unset", flex: "unset", padding: "unset" }
            : undefined
        }
      >
        <ButtonComponent
          type={"text"}
          onClick={goBack}
          icon={<Icon type={"chevron_left_small"} />}
          style={{ marginLeft: !isMobile ? "-12px" : "-26px" }}
        >
          {t("Back to Overview")}
        </ButtonComponent>
      </Col>

      <Col span={24}>
        <Flex justify="space-between" style={{ flexDirection: isMobile && "column" }}>
          <div style={{ paddingBottom: isMobile && 24 }}>
            <Title level={4} style={{ marginBottom: 24 }}>
              {t("Alerts by Bank")}
            </Title>
            <CardTotalPanel
              count={alertsBankData?.total_count}
              volume={alertsBankData?.total_volume}
              style={{ margin: 0 }}
              isMobile={isMobile}
            />
          </div>

          <DashboardFiltersBlock
            handleChangeFilter={handleChangeFilter}
            filters={filters}
            onlyPeriod={true}
          />
        </Flex>
      </Col>

      <Col span={24}>
        <Flex vertical gap={16} className="breakdown-table-header">
          <Title level={5} style={{ marginBottom: 0 }}>
            {t("Bank breakdown")}
          </Title>
          <div className="breakdown-search">
            <SearchComponent
              handleSearch={(e) => setBankSearch(e.target.value)}
              value={bankSearch}
              placeholder={t("Search bank")}
            />
          </div>
        </Flex>

        <Table
          className={"chart-table"}
          dataSource={filteredRows}
          columns={alertsByBankDetailColumns}
          onRow={getDashboardProgressRowProps}
          pagination={false}
          rowKey="label"
          tableLayout="fixed"
          scroll={isMobile ? { x: 770 } : undefined}
          showHeader={!!filteredRows.length}
          locale={{
            emptyText: bankSearch ? (
              <EmptyComponent type="search" style={{ minHeight: 450 }} />
            ) : (
              <EmptyComponent type="no_data_available" style={{ minHeight: 450 }} />
            )
          }}
        />
      </Col>
    </Row>
  )
}

const mapStateToProps = (state) => ({
  userInfo: state.auth.userInfo,
  dashboard: state.dashboard
})

const mapDispatchToProps = (dispatch) => ({
  getAlertsBankData: (params) => dispatch(Creators.getAlertsBankDataRequest(params)),
  changeFilters: (filters) => dispatch(Creators.dashboardSetProp("filters", filters))
})

export default connect(mapStateToProps, mapDispatchToProps)(AlertsByBankPage)
